import * as React from "react";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker, DateTimePickerProps } from "@mui/x-date-pickers/DateTimePicker";
import dayjs, { Dayjs } from "dayjs";
import type { SxProps, Theme } from "@mui/material";

type DateAcceptable = Date | string | number;

export interface DateTimeChangeArgs {
  value: Date | null;
  target: { value: Date | null };
  type: null;
}

interface DateTimeComponentOwnProps {
  sx?: SxProps<Theme>;
  value?: DateAcceptable | null;
  onChange?: (args: DateTimeChangeArgs) => void;
  format?: string;
  min?: DateAcceptable | null;
  max?: DateAcceptable | null;
  enabled?: boolean;
  disabled?: boolean;
  placeholder?: string;
  defaultValue?: DateAcceptable | null;
  maxDate?: DateAcceptable;
  maxDateTime?: DateAcceptable;
  minDate?: DateAcceptable;
  minDateTime?: DateAcceptable;
  width?: string;
  error?: boolean;
  name?: string;
  id?: string;
  slotProps?: DateTimePickerProps["slotProps"];
}

type DateTimePickerPassThroughProps = Omit<DateTimePickerProps, keyof DateTimeComponentOwnProps>;

export type DateTimeComponentProps = DateTimeComponentOwnProps & DateTimePickerPassThroughProps;

const DateTimeComponent = React.forwardRef<HTMLDivElement, DateTimeComponentProps>((props, ref) => {
  const localRef = React.useRef<HTMLDivElement>(null);
  const dateRef = (ref || localRef) as React.Ref<HTMLDivElement>;
  const {
    sx,
    value,
    onChange,
    format,
    min,
    max,
    enabled = true,
    disabled,
    placeholder: _placeholder,
    defaultValue,
    maxDate,
    maxDateTime,
    minDate,
    minDateTime,
    width,
    error,
    name,
    id,
    slotProps: externalSlotProps,
    ...otherProps
  } = props;

  // Convert syncfusion format to dayjs format
  const convertFormat = (syncFormat: string | undefined): string => {
    if (!syncFormat) return "DD/MM/YYYY hh:mm";
    // Common syncfusion to dayjs format conversions
    return syncFormat
      .replace(/dd/g, "DD")
      .replace(/MMM/g, "MMM")
      .replace(/MM/g, "MM")
      .replace(/yy/g, "YY")
      .replace(/yyyy/g, "YYYY")
      .replace(/HH/g, "HH")
      .replace(/hh/g, "hh")
      .replace(/mm/g, "mm");
  };

  const handleChange = (newValue: Dayjs | null): void => {
    const dateValue = newValue ? newValue.toDate() : null;
    if (onChange) {
      onChange({ value: dateValue, target: { value: dateValue }, type: null });
    }
  };

  const isDisabled = disabled !== undefined ? disabled : !enabled;

  // Support both syncfusion-style props (min/max) and MUI-style props (minDate/maxDate)
  const resolvedMinDateTime = min
    ? dayjs(min)
    : minDateTime
      ? dayjs(minDateTime)
      : minDate
        ? dayjs(minDate)
        : undefined;
  const resolvedMaxDateTime = max
    ? dayjs(max)
    : maxDateTime
      ? dayjs(maxDateTime)
      : maxDate
        ? dayjs(maxDate)
        : undefined;

  const externalTextFieldProps =
    externalSlotProps && typeof externalSlotProps.textField !== "function" ? externalSlotProps.textField : undefined;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DateTimePicker
        sx={{
          width: width ?? "100%",
          "&.MuiFormControl-root .MuiInputBase-root": {
            height: "2rem",
            background: "white",
          },
          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "var(--info-light-blue)",
            borderWidth: "0.0625rem",
          },
          ...sx,
        }}
        {...otherProps}
        defaultValue={defaultValue ? dayjs(defaultValue) : undefined}
        minDateTime={resolvedMinDateTime}
        maxDateTime={resolvedMaxDateTime}
        value={value ? dayjs(value) : null}
        onChange={handleChange}
        format={convertFormat(format)}
        disabled={isDisabled}
        slotProps={{
          ...externalSlotProps,
          // MUI X v9 dropped `placeholder` from the picker's textField slot — the field renders
          // editable format sections, so `format` is what shows while it is empty. The
          // `placeholder` prop stays on this wrapper's surface so call sites still compile.
          textField: {
            ...externalTextFieldProps,
            size: "small",
            error,
            name,
            id,
          },
        }}
        ref={dateRef}
      />
    </LocalizationProvider>
  );
});
DateTimeComponent.displayName = "DateTimeComponent";
export default DateTimeComponent;
